import {
  Building2,
  CheckCircle2,
  LoaderCircle,
  Pencil,
  Plus,
  Power,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import EliminarEmpresaModal from '../components/empresas/EliminarEmpresaModal'
import EmpresaModal from '../components/empresas/EmpresaModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useAuth } from '../context/AuthContext'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { formatearRut } from '../utils/rut'

function Empresas() {
  const { user } = useAuth()
  const { empresaActual, seleccionarEmpresa, recargarEmpresas } =
    useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()
  const navigate = useNavigate()

  const [empresasDetalle, setEmpresasDetalle] = useState([])
  const [rolesPorEmpresa, setRolesPorEmpresa] = useState({})
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalAbierto, setModalAbierto] = useState(false)
  const [empresaEditar, setEmpresaEditar] = useState(null)
  const [empresaEliminar, setEmpresaEliminar] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!user?.id) {
      setEmpresasDetalle([])
      setRolesPorEmpresa({})
      setCargando(false)
      return
    }

    cargarEmpresas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Se consulta directo (en vez de usar el listado ya filtrado de
  // EmpresaContext) porque este mantenedor también debe mostrar
  // empresas inactivas para poder reactivarlas o eliminarlas.
  const cargarEmpresas = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('usuarios_empresas')
        .select(`
          rol,
          empresa:empresas (
            id,
            razon_social,
            nombre_fantasia,
            rut,
            giro,
            direccion,
            comuna,
            ciudad,
            telefono,
            correo,
            activo,
            creado_en,
            actualizado_en
          )
        `)
        .eq('usuario_id', user.id)
        .eq('activo', true)

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      const relacionesValidas = (data || []).filter(
        (registro) => registro.empresa
      )

      const empresas = relacionesValidas
        .map((registro) => registro.empresa)
        .sort((a, b) =>
          (a.razon_social || '').localeCompare(b.razon_social || '')
        )

      setEmpresasDetalle(empresas)
      setRolesPorEmpresa(
        Object.fromEntries(
          relacionesValidas.map((registro) => [
            registro.empresa.id,
            registro.rol,
          ])
        )
      )
    } catch (errorCarga) {
      console.error('Error al cargar empresas:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar las empresas.'
      )
      setEmpresasDetalle([])
      setRolesPorEmpresa({})
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const empresasActivasCount = empresasDetalle.filter(
    (empresa) => empresa.activo
  ).length

  const puedeCrearEmpresa = Object.values(rolesPorEmpresa).some(
    (rol) => rol === 'administrador'
  )

  const empresasFiltradas = empresasDetalle.filter((empresa) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    const nombreCoincide =
      (empresa.razon_social || '')
        .toLowerCase()
        .includes(termino) ||
      (empresa.nombre_fantasia || '')
        .toLowerCase()
        .includes(termino)

    const rutCoincide = (empresa.rut || '')
      .toLowerCase()
      .includes(termino)

    return nombreCoincide || rutCoincide
  })

  const abrirCreacion = () => {
    setEmpresaEditar(null)
    setModalAbierto(true)
  }

  const abrirEdicion = (empresa) => {
    setEmpresaEditar(empresa)
    setModalAbierto(true)
  }

  const cerrarModalEmpresa = () => {
    setModalAbierto(false)
    setEmpresaEditar(null)
  }

  const alGuardar = (empresaGuardada) => {
    const fueEdicion = Boolean(empresaEditar)

    cerrarModalEmpresa()
    cargarEmpresas()

    abrirModal({
      tipo: 'success',
      titulo: fueEdicion ? 'Empresa actualizada' : 'Empresa creada',
      mensaje: fueEdicion
        ? `Los datos de "${empresaGuardada?.razon_social}" fueron actualizados correctamente.`
        : 'Empresa creada correctamente.',
    })
  }

  const usarEmpresa = (empresa) => {
    seleccionarEmpresa(empresa.id)
  }

  const ejecutarCambioActivo = async (empresa, nuevoActivo) => {
    try {
      const { error: errorActualizar } = await supabase
        .from('empresas')
        .update({ activo: nuevoActivo })
        .eq('id', empresa.id)
        .select()
        .single()

      if (errorActualizar) {
        throw errorActualizar
      }

      await cargarEmpresas()
      await recargarEmpresas()

      abrirModal({
        tipo: 'success',
        titulo: nuevoActivo ? 'Empresa reactivada' : 'Empresa desactivada',
        mensaje: nuevoActivo
          ? `"${empresa.nombre_fantasia}" fue reactivada correctamente.`
          : `"${empresa.nombre_fantasia}" fue desactivada correctamente.`,
      })
    } catch (errorCambio) {
      console.error('Error al cambiar el estado de la empresa:', errorCambio)

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar la empresa',
        mensaje:
          errorCambio.message ||
          'No fue posible actualizar el estado de la empresa.',
      })
    }
  }

  const confirmarDesactivar = (empresa, bloquearDesactivacion) => {
    if (bloquearDesactivacion) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Acción no permitida',
        mensaje: 'No puedes desactivar tu única empresa activa.',
      })
      return
    }

    abrirModal({
      tipo: 'confirm',
      titulo: 'Desactivar empresa',
      mensaje: `¿Deseas desactivar "${empresa.nombre_fantasia}"? Se conservará toda su información.`,
      textoAceptar: 'Desactivar',
      textoCancelar: 'Cancelar',
      mostrarCancelar: true,
      onAceptar: async () => {
        actualizarModal({
          procesando: true,
          mostrarCancelar: false,
          textoAceptar: 'Desactivando...',
        })

        await ejecutarCambioActivo(empresa, false)
      },
    })
  }

  const reactivarEmpresa = (empresa) => {
    ejecutarCambioActivo(empresa, true)
  }

  const abrirEliminar = (empresa) => setEmpresaEliminar(empresa)
  const cerrarEliminar = () => setEmpresaEliminar(null)

  const alEliminarEmpresa = async (empresaEliminada) => {
    cerrarEliminar()

    await recargarEmpresas()
    await cargarEmpresas()

    abrirModal({
      tipo: 'success',
      titulo: 'Empresa eliminada',
      mensaje: `La empresa "${
        empresaEliminada?.razon_social || ''
      }" fue eliminada correctamente.`,
    })

    navigate('/')
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando empresas...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarEmpresas}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (empresasDetalle.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Building2
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No tienes empresas asociadas
          </h3>

          <p className="mt-2 text-slate-500">
            No se encontraron empresas para tu usuario.
          </p>
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por nombre o RUT"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {empresasFiltradas.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron empresas para “{busqueda}”.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[860px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Empresa</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    RUT
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Tu rol
                  </th>
                  <th className="px-6 py-3">Estado</th>
                  <th className="px-6 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {empresasFiltradas.map((empresa) => {
                  const esAdministrador =
                    rolesPorEmpresa[empresa.id] ===
                    'administrador'

                  const esActiva =
                    empresa.id === empresaActual?.id

                  const bloquearDesactivacion =
                    empresa.activo && empresasActivasCount <= 1

                  return (
                    <tr key={empresa.id}>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900">
                          {empresa.nombre_fantasia}
                        </p>
                        <p className="text-xs text-slate-500">
                          {empresa.razon_social}
                        </p>
                      </td>

                      <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                        {empresa.rut
                          ? formatearRut(empresa.rut)
                          : '—'}
                      </td>

                      <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                        {rolesPorEmpresa[empresa.id] || '—'}
                      </td>

                      <td className="px-6 py-4">
                        {empresa.activo ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Activa
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                            Inactiva
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap justify-end gap-2">
                          {esActiva ? (
                            <span className="flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold text-emerald-700">
                              <CheckCircle2 size={16} />
                              Empresa activa
                            </span>
                          ) : empresa.activo ? (
                            <button
                              type="button"
                              onClick={() =>
                                usarEmpresa(empresa)
                              }
                              className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                              Usar esta empresa
                            </button>
                          ) : null}

                          {esAdministrador && (
                            <button
                              type="button"
                              onClick={() => abrirEdicion(empresa)}
                              title="Editar"
                              aria-label={`Editar empresa ${empresa.nombre_fantasia}`}
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <Pencil size={16} />
                            </button>
                          )}

                          {esAdministrador && empresa.activo && (
                            <button
                              type="button"
                              onClick={() =>
                                confirmarDesactivar(
                                  empresa,
                                  bloquearDesactivacion
                                )
                              }
                              title="Desactivar"
                              aria-label={`Desactivar empresa ${empresa.nombre_fantasia}`}
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <Power size={16} />
                            </button>
                          )}

                          {esAdministrador && !empresa.activo && (
                            <button
                              type="button"
                              onClick={() => reactivarEmpresa(empresa)}
                              title="Reactivar"
                              aria-label={`Reactivar empresa ${empresa.nombre_fantasia}`}
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}

                          {esAdministrador && (
                            <button
                              type="button"
                              onClick={() => abrirEliminar(empresa)}
                              title="Eliminar definitivamente"
                              aria-label={`Eliminar definitivamente la empresa ${empresa.nombre_fantasia}`}
                              className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Mantenedores
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Empresas
          </h2>

          <p className="mt-2 text-slate-500">
            Administra las empresas asociadas a tu usuario.
          </p>
        </div>

        {puedeCrearEmpresa && (
          <button
            type="button"
            onClick={abrirCreacion}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nueva empresa
          </button>
        )}
      </div>

      {renderContenido()}

      {modalAbierto && (
        <EmpresaModal
          empresa={empresaEditar}
          onClose={cerrarModalEmpresa}
          onGuardado={alGuardar}
        />
      )}

      {empresaEliminar && (
        <EliminarEmpresaModal
          empresa={empresaEliminar}
          onClose={cerrarEliminar}
          onEliminada={alEliminarEmpresa}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Empresas
